import { valibotResolver } from "@hookform/resolvers/valibot";
import { ArrowUpRight, PencilSimpleLine, Warning } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Skeleton from "common/components/ui/Skeleton";
import { AGENCY_CHROME_EXTENSION_ID } from "common/constants";
import { getChromeExtensionUrl } from "common/helpers/utils";
import useChromeExtension from "common/hooks/useChromeExtension";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../../datahooks/useAgencySettingsMutations";
import { ExtensionIDSchema, type ExtensionIDSchemaType } from "../../../schema";

export default function ExtensionID() {
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);
  const [extensionState, setExtensionState] = useState<
    { error: string } | { name: string }
  >();

  const { agencySettings } = useAgencySettings();
  const { pingExtension } = useChromeExtension();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: { id: agencySettings ? agencySettings.extension_id : "" },
    resolver: valibotResolver(ExtensionIDSchema),
  });

  useEffect(() => {
    if (agencySettings) {
      const { extension_id } = agencySettings;
      reset({ id: extension_id });

      if (extension_id) {
        pingExtension(extension_id)
          .then((response) => {
            if (response.status === "ok") {
              setExtensionState({ name: response.response.name });
            } else {
              setExtensionState({ error: response.error });
            }
          })
          .catch(() => {
            setExtensionState({
              error:
                "Extension not installed. Install from Web Store to validate",
            });
          });
      }
    }
  }, [agencySettings]);

  async function onSubmit({ id }: ExtensionIDSchemaType) {
    if (id) {
      try {
        const response = await pingExtension(id);
        if (response.status === "ok") {
          setExtensionState({ name: response.response.name });
        } else {
          setError("id", {
            message: response.error,
          });
          return;
        }
      } catch {
        setError("id", {
          message:
            "Error validating id, please check the id and ensure the extension is installed",
        });
        return;
      }
    }

    await updateAgencySettings({
      updates: { extension_id: id },
    });
    setIsEditing(false);
  }

  function onCancel() {
    setIsEditing(false);
    reset({ id: agencySettings.extension_id });
  }

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <FormField
          label="Custom extension ID"
          htmlFor="extension-id"
          className="w-full"
          error={errors.id?.message}
          size={isDesktop ? "sm" : "lg"}
        >
          <div className="gap-x-2 gap-y-4 flex flex-col lg:flex-row">
            <Input
              id="extension-id"
              placeholder="Paste custom extension ID"
              variant={isDesktop ? "sm" : "lg"}
              className="lg:w-80"
              autoFocus
              {...register("id")}
            />
            <div className="flex gap-x-2 mt-auto ml-auto">
              <Button
                variant="secondary-black"
                size={isDesktop ? "sm" : "md"}
                onClick={onCancel}
                disabled={isUpdatingAgencySettings}
              >
                Cancel
              </Button>
              <Button
                size={isDesktop ? "sm" : "md"}
                type="submit"
                variant="secondary-purple"
                isLoading={isUpdatingAgencySettings}
              >
                Save
              </Button>
            </div>
          </div>
        </FormField>
      </form>
    );
  }

  const isError = extensionState && "error" in extensionState;

  let extensionSubtitle =
    "You are currently using our brand-neutral Chrome extension";
  let extensionNameComponent = (
    <span className="text-button-sm">Generic chrome extension</span>
  );

  if (agencySettings.extension_id) {
    if (!extensionState) {
      extensionNameComponent = <Skeleton className="h-5 w-30" />;
    } else {
      extensionSubtitle = isError
        ? extensionState.error
        : "You are currently using your personalized custom Chrome extension";

      extensionNameComponent = (
        <span className="text-button-sm">
          {"name" in extensionState ? extensionState.name : "Custom extension"}
        </span>
      );
    }
  }

  return (
    <div className="flex justify-between items-center">
      <div
        className={clsx(
          isError ? "border-yellow-400" : "border-black-100",
          "py-2 pl-4 pr-3 flex items-center gap-x-2 border bg-whiteGray rounded-4xl w-112",
        )}
      >
        <div className="flex-1 flex flex-col gap-y-0.5">
          <div className="flex items-center gap-x-1">
            {extensionNameComponent}
            <RenderIf condition={isError}>
              <Warning className="text-yellow-500 size-4 " weight="fill" />
            </RenderIf>
          </div>
          <span className="text-xs text-black-500">{extensionSubtitle}</span>
        </div>
        <Button
          variant="outline-black"
          size="sm"
          rightIcon={!isError && <ArrowUpRight />}
          asChild
        >
          <Link
            to={getChromeExtensionUrl(
              agencySettings.extension_id || AGENCY_CHROME_EXTENSION_ID,
            )}
            target="_blank"
          >
            {isError ? "Install now" : "Web Store"}
          </Link>
        </Button>
      </div>
      <Button
        variant="ghost-black"
        intent="iconOnly"
        className="ml-auto"
        onClick={() => setIsEditing(true)}
      >
        <PencilSimpleLine />
      </Button>
    </div>
  );
}
